import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL, getWsUrl } from '../../services/config';
import organizationService from '../../services/organizationService';
import '../../components/RoomList/RoomList.css';

const RoomList = () => {
    const { t } = useTranslation();
    const [rooms, setRooms] = useState([]);
    const wsRef = useRef(null);
    const isUnmountingRef = useRef(false);
    const retryAttemptRef = useRef(0);
    const retryTimeoutRef = useRef(null);
    const pollIntervalRef = useRef(null);
    const orgListenerRef = useRef(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Listen for organization changes
    useEffect(() => {
        orgListenerRef.current = organizationService.addListener(() => {
            // Close existing WebSocket
            if (wsRef.current) {
                try { wsRef.current.close(); } catch (_) {}
                wsRef.current = null;
            }
            // Clear any pending retries
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
                retryTimeoutRef.current = null;
            }
            // Reset retry attempt
            retryAttemptRef.current = 0;
            // Trigger refresh by updating state
            setRefreshTrigger(prev => prev + 1);
        });

        return () => {
            if (orgListenerRef.current) {
                orgListenerRef.current();
            }
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const fetchRoomsHttp = async () => {
            try {
                const token = localStorage.getItem('token');
                const currentOrg = organizationService.getOrganization();
                
                // Build URL with organization filter
                let url = `${API_BASE_URL}/api/resources/`;
                if (currentOrg) {
                    url += `?organization_id=${currentOrg.id}`;
                }
                
                const response = await axios.get(url, {
                    headers: {
                        'Authorization': `Token ${token}`,
                        'Content-Type': 'application/json',
                    },
                });
                setRooms(response.data);
            } catch (error) {
                // Swallow errors during backend downtime; we'll retry later
            }
        };

        const startPollingFallback = () => {
            if (pollIntervalRef.current) return;
            // Poll every 15s while WS is disconnected
            pollIntervalRef.current = setInterval(fetchRoomsHttp, 15000);
        };

        const stopPollingFallback = () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
            }
        };

        // Initial HTTP fetch as a fallback while WS connects
        fetchRoomsHttp();

        const connect = () => {
            if (isUnmountingRef.current) return;
            // Avoid duplicate sockets across re-mounts/StrictMode
            if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
                return;
            }

            const token = localStorage.getItem('token');
            const currentOrg = organizationService.getOrganization();
            
            // Build WebSocket URL with token and organization_id
            let query = token ? `?token=${encodeURIComponent(token)}` : '?';
            if (currentOrg) {
                query += `${token ? '&' : ''}organization_id=${currentOrg.id}`;
            }
            
            const wsUrl = getWsUrl(`/ws/rooms/${query}`);
            const ws = new WebSocket(wsUrl);
            wsRef.current = ws;

            ws.onopen = () => {
                retryAttemptRef.current = 0;
                if (retryTimeoutRef.current) {
                    clearTimeout(retryTimeoutRef.current);
                    retryTimeoutRef.current = null;
                }
                stopPollingFallback();
            };

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.type === 'rooms.list' && Array.isArray(data.rooms)) {
                        setRooms(data.rooms);
                    } else if (data.type === 'rooms.updated' && data.payload) {
                        const { action, resource } = data.payload;
                        setRooms((prev) => {
                            if (action === 'created') {
                                const exists = prev.some((r) => r.id === resource.id);
                                return exists ? prev.map((r) => (r.id === resource.id ? { ...r, ...resource } : r)) : [resource, ...prev];
                            }
                            if (action === 'updated') {
                                return prev.map((r) => (r.id === resource.id ? { ...r, ...resource } : r));
                            }
                            if (action === 'deleted') {
                                return prev.filter((r) => r.id !== resource.id);
                            }
                            return prev;
                        });
                    }
                } catch (_) {
                    // ignore invalid payloads
                }
            };

            ws.onerror = () => {
                // Errors are expected during downtime; onclose will handle reconnect
            };

            ws.onclose = () => {
                if (isUnmountingRef.current) return;
                scheduleReconnect();
            };
        };

        const scheduleReconnect = () => {
            if (isUnmountingRef.current) return;
            // Start HTTP polling while socket is down
            startPollingFallback();

            const attempt = Math.min(retryAttemptRef.current + 1, 10);
            retryAttemptRef.current = attempt;
            // Exponential backoff (1s, 2s, 4s, ... capped at 30s) + jitter up to 1s
            const backoff = Math.min(30000, 1000 * Math.pow(2, attempt - 1));
            const jitter = Math.floor(Math.random() * 1000);
            const delay = backoff + jitter;

            if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = setTimeout(() => {
                // Try an immediate HTTP fetch before reconnecting to keep UI fresher
                fetchRoomsHttp();
                connect();
            }, delay);
        };

        const handleOnline = () => {
            // When browser goes online, try immediate reconnect
            if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) {
                if (retryTimeoutRef.current) {
                    clearTimeout(retryTimeoutRef.current);
                    retryTimeoutRef.current = null;
                }
                connect();
            }
        };

        const handleVisibility = () => {
            if (document.visibilityState === 'visible') {
                if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) {
                    connect();
                }
            }
        };

        window.addEventListener('online', handleOnline);
        document.addEventListener('visibilitychange', handleVisibility);

        // Kick off initial connection
        connect();

        return () => {
            isUnmountingRef.current = true;
            window.removeEventListener('online', handleOnline);
            document.removeEventListener('visibilitychange', handleVisibility);
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
                retryTimeoutRef.current = null;
            }
            stopPollingFallback();

            const current = wsRef.current;
            if (current) {
                if (current.readyState === WebSocket.CONNECTING) {
                    // Defer close to avoid browser error spam
                    current.addEventListener('open', () => {
                        try { current.close(); } catch (_) {}
                    }, { once: true });
                } else if (current.readyState === WebSocket.OPEN) {
                    try { current.close(); } catch (_) {}
                }
            }
            wsRef.current = null;
            // Allow future mounts to proceed normally
            setTimeout(() => { isUnmountingRef.current = false; }, 0);
        };
    }, [refreshTrigger]);

    return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
            <h1>{t('rooms_available')}</h1>
            <ul className="rooms-grid">
                {rooms.map((room) => (
                    <li key={room.id ?? room.name}>
                        <Link to={`/room/${room.id}`} className="room-card" style={{ backgroundColor: room.color }}>
                            <h2 style={{ margin: '0 0 10px 0' }}>{room.name || room.id}</h2>
                            <p style={{ margin: '0', color: '#666' }}>{t('rooms_click_details')}</p>
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default RoomList;
